// src/components/LHPProduksiHarian.tsx
import React from 'react';
import { Box, Text } from '@chakra-ui/react';
import {
    ResponsiveContainer,
    ComposedChart,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Bar,
    Line,
    Scatter,
} from 'recharts';
import { DataKey } from 'recharts/types/util/types';
import { formatNumber } from '../produksi';
import { LHPTrendDatum } from './LHPProduksiTrend';
import { CustomTooltipDailyLhp } from '../CustomTooltip';

export interface LHPHarianDatum {
    periode: string;
    date: string;
    Restan: number;
    Internal: number;
    Eksternal: number;
    Olah: number;
    'Jam Olah': number;
}

interface Props {
    data: LHPHarianDatum[];
}

const COLORS = {
    Internal: '#82CA9D',
    Eksternal: '#8884D8',
    Olah: '#FFC658',
    Throughput: '#FF7300',
    Black: '#000000',
};

const LHPProduksiHarian: React.FC<Props> = ({ data }) => {
    const [hoveringDataKey, setHoveringDataKey] = React.useState<DataKey<any> | undefined>(undefined);

    let internalOpacity = 1;
    let eksternalOpacity = 1;
    let olahOpacity = 1;
    let jamOlahOpacity = 1;
    let throughputOpacity = 1;

    if (hoveringDataKey === 'Internal') {
        eksternalOpacity = 0.5;
        olahOpacity = 0.5;
        jamOlahOpacity = 0.5;
        throughputOpacity = 0.5;
    } else if (hoveringDataKey === 'Eksternal') {
        internalOpacity = 0.5;
        olahOpacity = 0.5;
        jamOlahOpacity = 0.5;
        throughputOpacity = 0.5;
    } else if (hoveringDataKey === 'Olah') {
        internalOpacity = 0.5;
        eksternalOpacity = 0.5;
        jamOlahOpacity = 0.5;
        throughputOpacity = 0.5;
    } else if (hoveringDataKey === 'jamOlah') {
        internalOpacity = 0.5;
        eksternalOpacity = 0.5;
        olahOpacity = 0.5;
        throughputOpacity = 0.5;
    } else if (hoveringDataKey === 'throughput') {
        internalOpacity = 0.5;
        eksternalOpacity = 0.5;
        olahOpacity = 0.5;
        jamOlahOpacity = 0.5;
    }

    const handleMouseEnter = (payload: any) => {
        setHoveringDataKey(payload.dataKey);
    };

    const handleMouseLeave = () => {
        setHoveringDataKey(undefined);
    };
    const scaledData = data.map(d => {
        const Internal = d.Internal / 1000;
        const Eksternal = d.Eksternal / 1000;
        const Olah = d.Olah / 1000;
        const jamOlah = d['Jam Olah'];
        const intPlusExt = Internal + Eksternal;
        // Throughput = Olah / Jam Olah (in Ton/Jam)
        const throughput = jamOlah > 0 ? Olah / jamOlah : 0;
        
        return {
            ...d,
            Internal,
            Eksternal,
            Olah,
            jamOlah,
            intPlusExt,
            throughput,
        };
    });

    // fallback ke bulan/tahun sekarang jika data kosong
    const now = new Date();
    const defaultYear = now.getFullYear().toString();
    const defaultMonthNum = String(now.getMonth() + 1).padStart(2, '0');

    const rawPeriod = data[0]?.periode;
    const year = rawPeriod ? rawPeriod.slice(0, 4) : defaultYear;
    const monthNum = rawPeriod ? rawPeriod.slice(4) : defaultMonthNum;

    const monthNames = [
        'Januari','Februari','Maret','April','Mei','Juni',
        'Juli','Agustus','September','Oktober','November','Desember'
    ];
    const monthName = monthNames[parseInt(monthNum, 10) - 1];

    return (
        <Box p={4} display="flex" flexDirection="column" boxShadow="md" height="100%" overflowX="auto" overflowY="hidden">
            <Text fontSize="xl" fontWeight="semibold" mb={4} color="gray.700">
                Produksi TBS Harian ({monthName} {year})
            </Text>
            <Box
                // flex={1} 
                minH="130px" 
                height="350px"
                minWidth="700px"
                overflowX="auto" 
                overflowY="hidden"
                w="100%"
            >
                <ResponsiveContainer width="100%" height={400}>
                    <ComposedChart data={scaledData} barGap={0}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis
                            dataKey="date"
                            // interval={0}
                            height={80}
                            tick={{ fontSize: 12 }}
                        />

                        <YAxis
                            yAxisId="left"
                            tickFormatter={v => formatNumber(Number(v), 'number')}
                            tick={{ fontSize: 12 }}
                            label={{ value: 'Ton', angle: -90, position: 'insideLeft', offset: 5 }}
                        />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 12 }}
                            tickFormatter={v => formatNumber(Number(v), 'number')}
                            label={{ value: 'Throughput (Ton/Jam)', angle: 90, position: 'insideCenter', offset: 30 }}
                        />

                        <Tooltip
                            content={<CustomTooltipDailyLhp />}
                            formatter={(value: any, name: string) => formatNumber(Number(value), name === 'Jam Olah' ? 'number' : 'number')}
                            wrapperStyle={{ zIndex: 1000 }}
                        />
                        <Legend
                            verticalAlign="bottom"
                            align="center"
                            height={30}
                            wrapperStyle={{ bottom: 60 }}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        />

                        {/* Stacked bars */}
                        <Bar
                            yAxisId="left"
                            name="TBS Internal"
                            dataKey="Internal"
                            stackId="a"
                            fill={COLORS.Internal}
                            fillOpacity={internalOpacity}
                        />
                        <Bar
                            yAxisId="left"
                            name="TBS Eksternal"
                            dataKey="Eksternal"
                            stackId="a"
                            fill={COLORS.Eksternal}
                            fillOpacity={eksternalOpacity}
                        />

                        {/* Bar for Olah and Jam Olah */}
                        <Bar
                            yAxisId="left"
                            name="TBS Olah"
                            dataKey="Olah"
                            fill={COLORS.Olah}
                            fillOpacity={olahOpacity}
                        />

                        <Scatter
                            yAxisId="right"
                            name="Jam Olah"
                            dataKey="jamOlah"
                            fill={COLORS.Black}
                            shape="cross"
                        />

                        {/* Line for Throughput */}
                        <Line
                            yAxisId="right"
                            name="Throughput"
                            type="monotone"
                            dataKey="throughput"
                            stroke={COLORS.Throughput}
                            strokeWidth={2}
                            strokeOpacity={throughputOpacity}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};

export default LHPProduksiHarian;
